<script setup lang="ts">

import EditComment from "@/Pages/assessments/components/EditComment.vue";
import SubmitComment from "@/Pages/assessments/components/SubmitComment.vue";
import {AnswerComment} from "@/types";
import {router} from "@inertiajs/vue3";
import {route} from "ziggy-js";
import {computed, ref} from "vue";
import {useDate} from "@/composables/date";

const {lastUsed} = useDate()

interface Props {
    comments: Array<AnswerComment>
    assessmentId: number
    answerId: number
    open?: boolean
    readonly?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    open: false,
    readonly: false
})

const isOpen = ref<boolean>(props.open);

const icon = computed(() => {
    return isOpen.value ? 'mdi-chevron-down' : 'mdi-chevron-right';
})

const addComment = (comment: string) => {
    router.post(route('answers-comments.store', {assessment: props.assessmentId, answer: props.answerId}), {
        comment: comment
    }, {
        preserveScroll: true,
    })
}

const updateComment = (commentId: number, comment: string) => {
    router.put(route('answers-comments.update', {assessment: props.assessmentId, answer: props.answerId, answerComment: commentId}), {
        comment: comment
    }, {
        preserveScroll: true,
    })
}

const deleteComment = (commentId: number) => {
    router.delete(route('answers-comments.destroy', {assessment: props.assessmentId, answer: props.answerId, answerComment: commentId}), {
        preserveScroll: true,
    })
}
</script>

<template>
    <v-container
        fluid
        class="pa-0"
    >
        <div class="text-body-2 d-flex align-center">
            <v-icon-btn
                :icon="icon"
                rounded="lg"
                color="transparent"
                icon-size="small"
                size="small"
                class="mr-2 ml-2"
                @click="isOpen = !isOpen"
            />
            <v-btn
                variant="text"
                size="x-small"
                class="px-0 mr-1"
                @click="isOpen = !isOpen"
            >
                <span
                    v-if="!isOpen"
                    class="font-weight-regular"
                >
                    {{ props.comments.length }} {{ props.comments.length > 1 ? 'internal comments' : 'internal comment' }}
                </span>
                <span
                    v-else
                    class="font-weight-regular"
                >
                    Collapse internal comments
                </span>
            </v-btn>
            <span
                v-if="props.comments.length > 0 && !isOpen"
                class="text-medium-emphasis"
            >
                Last reply by {{ props.comments[props.comments.length - 1].user_name }} {{ lastUsed(props.comments[props.comments.length - 1].created_at) }}
            </span>
        </div>

        <v-expand-transition>
            <v-row v-if="isOpen">
                <v-col
                    cols="12"
                    class="pb-0"
                >
                    <v-timeline
                        side="end"
                        density="compact"
                        align="start"
                        class="bg-transparent w-100"
                    >
                        <v-timeline-item
                            v-for="comment in props.comments"
                            :key="`answer-${props.answerId}-comment-${comment.id}`"
                            side="end"
                            width="100%"
                            class="bg-transparent"
                        >
                            <template #icon>
                                <v-avatar
                                    size="small"
                                    :image="`https://ui-avatars.com/api/?name=${comment.user_name}`"
                                />
                            </template>
                            <edit-comment
                                :comment="comment"
                                :readonly="props.readonly"
                                @update="updateComment(comment.id, $event)"
                                @delete="deleteComment(comment.id)"
                            />
                        </v-timeline-item>
                    </v-timeline>
                </v-col>
                <v-col cols="12">
                    <submit-comment
                        v-if="!props.readonly"
                        @submit="addComment($event)"
                    />
                </v-col>
            </v-row>
        </v-expand-transition>
    </v-container>
</template>

<style scoped>

</style>
